import React, { useState, useLayoutEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { getConfigDetails, createConfig, updateConfig, getApplications, getUserOrganizations } from '@/api'
import { CheckIcon } from '@/components/Table/components'
import { FormLabel, FormControl, FormHelperText, Checkbox, FormGroup, Stack, TextField } from '@mui/material'
import TagInput from '@/components/TagInput'
import { usePath } from '@/hooks'
import { PlainCard, Row, LabeledCheckbox, ScrollY } from './components'
import Text from '@/components/Text'
import Button from '@/components/Button'
import {
  WebhookProps,
  MessageChannelTypes,
  MessageChannelEnum,
  ForwardingConfigDetails,
  ForwardingConfigType,
} from '@/types'
import EmailConfig from './emailForm'
import WebhookConfig from './webhookForm'
import SyslogConfig from './sysForm'
import { Message } from '@cx/ui'

type ConfigExpandStatus = {
  emails: boolean
  ding_talks: boolean
  we_coms: boolean
  larks: boolean
  syslog: boolean
}

const ConfigForm: React.FC = () => {
  const navigate = useNavigate()
  const pathObject = usePath()
  const formId = pathObject?.path === 'createConfig' ? null : parseInt(pathObject?.path)

  const [configType, setConfigType] = useState<ForwardingConfigType>()
  const [config, setConfig] = useState<ForwardingConfigDetails>({
    desc: '',
    org_ids: [],
    app_ids: [],
    channels: [],
  })
  const [messageChannels, setMessageChannels] = useState<MessageChannelTypes[]>([])
  const [validate, setValidate] = useState<boolean>(false)

  const [expandStatus, setExpandStatus] = useState<ConfigExpandStatus>({
    emails: false,
    ding_talks: false,
    we_coms: false,
    larks: false,
    syslog: false,
  })
  const onCancel = useCallback(() => {
    navigate('/console/account/notifications/config')
  }, [navigate])

  const { data: market } = useQuery(['getApplications'], getApplications, {})

  const { data: orgs } = useQuery(['getUserOrganizationsMenu'], () => getUserOrganizations())

  const { data: configRes } = useQuery(
    ['getConfigDetails', formId],
    // @ts-ignore
    () => getConfigDetails(formId),
    {
      enabled: !!formId,
      onSuccess: (res) => {
        setConfigType(res?.type)
        setConfig(() => ({
          ...res,
          org_ids: res?.orgs?.filter((org) => org.name?.length > 0).map((org) => org?.id) ?? [],
          app_ids: res?.apps?.map((app) => app?.id) ?? [],
        }))

        if (res?.type === 'normal') {
          const channelsSet = new Set(messageChannels)

          const channelsDetail = [...res.channels]

          for (const channel of channelsDetail) {
            channelsSet.add(channel.channel as MessageChannelTypes)
            channel.addresses = channel?.addresses?.map((address, index) => ({
              id: `webhook-${index}`,
              address: address?.address,
              sign: address?.sign,
            }))
          }

          setConfig(() => ({
            ...res,
            org_ids: res?.orgs?.map((org) => org?.id) ?? [],
            app_ids: res?.apps?.map((app) => app?.id) ?? [],
            channels: channelsDetail,
          }))
          setMessageChannels(Array.from(channelsSet))
        }
      },
    },
  )

  const { mutate: createMutate } = useMutation(createConfig, {
    onSuccess: () => {
      Message.success('创建转发配置成功')
      onCancel()
    },
  })

  const { mutate: updateMutate } = useMutation(updateConfig, {
    onSuccess: () => {
      Message.success('更新转发配置成功')
      onCancel()
    },
  })

  const deleteConfigChannel = (type: MessageChannelTypes) => {
    const updateChannels = [...config?.channels]
    const channelIndex = updateChannels?.findIndex((channel) => channel.channel === type)
    updateChannels.splice(channelIndex, 1)
    setConfig((config) => ({ ...config, channels: [...updateChannels] }))
  }

  const initiateChannel = (type: MessageChannelTypes) => {
    const updateChannels = [...config?.channels]
    const channelIndex = updateChannels?.findIndex((channel) => channel.channel === type)
    if (channelIndex > -1) {
      updateChannels.splice(channelIndex, 1)
    }
    updateChannels?.push({
      channel: type,
      addresses: type === 'email' || type === 'syslog' ? [] : [{ id: 'webhook-0', address: '', sign: '' }],
    })
    setConfig((config) => ({ ...config, channels: [...updateChannels] }))
  }

  const handleChangeMessageChannels = (type: MessageChannelTypes, checked: boolean) => {
    const channelsSet = new Set(messageChannels)
    switch (type) {
      case 'email': {
        setExpandStatus((status) => ({
          ...status,
          emails: checked,
          ...(checked && {
            ding_talks: false,
            we_coms: false,
            larks: false,
            syslog: false,
          }),
        }))
        setConfig((config) => ({ ...config, is_email: checked ? 1 : 0 }))

        if (checked) {
          channelsSet.add('email')
          initiateChannel('email')
        } else {
          channelsSet.delete('email')
          deleteConfigChannel('email')
        }
        setMessageChannels(Array.from(channelsSet))
        break
      }
      case 'syslog': {
        setExpandStatus((status) => ({
          ...status,
          syslog: checked,
          ...(checked && {
            emails: false,
            ding_talks: false,
            we_coms: false,
            larks: false,
          }),
        }))
        setConfig((config) => ({ ...config, is_email: checked ? 1 : 0 }))
        if (checked) {
          channelsSet.add('syslog')
          initiateChannel('syslog')
        } else {
          channelsSet.delete('syslog')
          deleteConfigChannel('syslog')
        }
        setMessageChannels(Array.from(channelsSet))
        break
      }
      case 'dingtalk': {
        setExpandStatus((status) => ({
          ...status,
          ding_talks: checked,
          ...(checked && {
            emails: false,
            we_coms: false,
            larks: false,
            syslog: false,
          }),
        }))
        if (checked) {
          channelsSet.add('dingtalk')
          initiateChannel('dingtalk')
        } else {
          channelsSet.delete('dingtalk')
          deleteConfigChannel('dingtalk')
        }
        setMessageChannels(Array.from(channelsSet))
        break
      }
      case 'wecom': {
        setExpandStatus((status) => ({
          ...status,
          we_coms: checked,
          ...(checked && {
            emails: false,
            ding_talks: false,
            larks: false,
            we_chats: false,
            syslog: false,
          }),
        }))
        if (checked) {
          channelsSet.add('wecom')
          initiateChannel('wecom')
        } else {
          channelsSet.delete('wecom')
          deleteConfigChannel('wecom')
        }
        setMessageChannels(Array.from(channelsSet))
        break
      }
      case 'lark': {
        setExpandStatus((status) => ({
          ...status,
          larks: checked,
          ...(checked && {
            emails: false,
            ding_talks: false,
            we_coms: false,
            we_chats: false,
            syslog: false,
          }),
        }))
        if (checked) {
          channelsSet.add('lark')
          initiateChannel('lark')
        } else {
          channelsSet.delete('lark')
          deleteConfigChannel('lark')
        }
        setMessageChannels(Array.from(channelsSet))
        break
      }
    }
  }

  const updateChannelWebhooks = (type: MessageChannelTypes, webhooks: WebhookProps[]) => {
    const updateChannels = [...config?.channels]
    const channelIndex = updateChannels?.findIndex((channel) => channel.channel === type)
    if (webhooks?.length === 0) {
      updateChannels.splice(channelIndex, 1)
    } else {
      if (channelIndex === -1) {
        updateChannels.push({ channel: type, addresses: webhooks })
      } else {
        updateChannels[channelIndex] = { channel: type, addresses: webhooks }
      }
    }
    setConfig((config) => ({ ...config, channels: [...updateChannels] }))
  }

  const handleUpdateWebhooks = (type: MessageChannelTypes, webhooks: WebhookProps[]) => {
    const channelsSet = new Set(messageChannels)
    switch (type) {
      case 'email': {
        updateChannelWebhooks('email', webhooks)
        break
      }
      case 'dingtalk': {
        if (webhooks?.length == 0) {
          channelsSet.delete('dingtalk')
        }
        updateChannelWebhooks('dingtalk', webhooks)
        setMessageChannels(Array.from(channelsSet))
        break
      }
      case 'wecom': {
        if (webhooks?.length == 0) {
          channelsSet.delete('wecom')
        }
        updateChannelWebhooks('wecom', webhooks)
        setMessageChannels(Array.from(channelsSet))
        break
      }
      case 'lark':
        {
          if (webhooks?.length == 0) {
            channelsSet.delete('lark')
          }
          updateChannelWebhooks('lark', webhooks)
        }
        setMessageChannels(Array.from(channelsSet))
        break
      case 'syslog': {
        if (webhooks?.length == 0) {
          channelsSet.delete('syslog')
        }
        updateChannelWebhooks('syslog', webhooks)
        setMessageChannels(Array.from(channelsSet))
        break
      }
    }
  }

  const messageChannelComponents: {
    name: MessageChannelTypes
    element: React.ReactNode
  }[] = [
    {
      name: 'email',
      element: (
        <EmailConfig
          validate={messageChannels?.includes('email') && validate}
          id='email'
          max={10}
          key='config-email'
          value={
            config?.channels
              ?.find((channel) => channel.channel === 'email')
              ?.addresses?.map((address) => address.address) ?? []
          }
          //eslint-disable-next-line react/jsx-no-bind
          onChange={(emails: string[]) => {
            handleUpdateWebhooks(
              'email',
              emails?.map((email, index) => ({
                id: `webhook-${index}`,
                address: email,
                sign: '',
              })),
            )
          }}
          expand={expandStatus?.emails}
          //eslint-disable-next-line react/jsx-no-bind
          onExpand={(expanded: boolean) => setExpandStatus((status) => ({ ...status, emails: expanded }))}
        />
      ),
    },
    {
      name: 'dingtalk',
      element: (
        <WebhookConfig
          id='dingtalk'
          validate={messageChannels?.includes('dingtalk') && validate}
          webhookType={MessageChannelEnum.dingtalk}
          key='config-dingtalk'
          name={'钉钉'}
          helperText={'提醒：请在钉钉群设置/群机器人中添加并获取机器人 webhook 信息。'}
          helperLink={'https://open.dingtalk.com/document/robots/custom-robot-access'}
          value={config?.channels?.find((channel) => channel.channel === 'dingtalk')?.addresses ?? []}
          //eslint-disable-next-line react/jsx-no-bind
          onChange={(webhooks) => handleUpdateWebhooks('dingtalk', webhooks)}
          expand={expandStatus?.ding_talks}
          //eslint-disable-next-line react/jsx-no-bind
          onExpand={(expanded: boolean) => setExpandStatus((status) => ({ ...status, ding_talks: expanded }))}
        />
      ),
    },
    {
      name: 'wecom',
      element: (
        <WebhookConfig
          id='wecom'
          validate={messageChannels?.includes('wecom') && validate}
          webhookType={MessageChannelEnum.wecom}
          hideSignInput={true}
          key='config-wecom'
          name={'企业微信'}
          helperText={'提醒：请在企业微信群设置/群机器人中添加并获取机器人 webhook 信息。'}
          helperLink={'https://developer.work.weixin.qq.com/document/path/91770'}
          value={config?.channels?.find((channel) => channel.channel === 'wecom')?.addresses ?? []}
          //eslint-disable-next-line react/jsx-no-bind
          onChange={(webhooks) => handleUpdateWebhooks('wecom', webhooks)}
          expand={expandStatus?.we_coms}
          //eslint-disable-next-line react/jsx-no-bind
          onExpand={(expanded: boolean) => setExpandStatus((status) => ({ ...status, we_coms: expanded }))}
        />
      ),
    },
    {
      name: 'lark',
      element: (
        <WebhookConfig
          id='lark'
          validate={messageChannels?.includes('lark') && validate}
          webhookType={MessageChannelEnum.lark}
          key='config-lark'
          name={'飞书'}
          helperText={'提醒：请在飞书群设置/群机器人中添加并获取机器人 webhook 信息。'}
          helperLink={'https://open.feishu.cn/document/ukTMukTMukTM/ucTM5YjL3ETO24yNxkjN'}
          value={config?.channels?.find((channel) => channel.channel === 'lark')?.addresses ?? []}
          //eslint-disable-next-line react/jsx-no-bind
          onChange={(webhooks) => handleUpdateWebhooks('lark', webhooks)}
          expand={expandStatus?.larks}
          //eslint-disable-next-line react/jsx-no-bind
          onExpand={(expanded: boolean) => setExpandStatus((status) => ({ ...status, larks: expanded }))}
        />
      ),
    },
    {
      name: 'syslog',
      element: (
        <SyslogConfig
          validate={messageChannels?.includes('syslog') && validate}
          id='email'
          max={10}
          key='config-syslog'
          value={
            config?.channels
              ?.find((channel) => channel.channel === 'syslog')
              ?.addresses?.map((address) => address.address) ?? []
          }
          //eslint-disable-next-line react/jsx-no-bind
          onChange={(syslogs: string[]) => {
            handleUpdateWebhooks(
              'syslog',
              syslogs?.map((syslog, index) => ({
                id: `webhook-${index}`,
                address: syslog,
                sign: '',
              })),
            )
          }}
          expand={expandStatus?.syslog}
          //eslint-disable-next-line react/jsx-no-bind
          onExpand={(expanded: boolean) => setExpandStatus((status) => ({ ...status, syslog: expanded }))}
        />
      ),
    },
  ]

  const renderMessageChannelForm = (channels: MessageChannelTypes[]) => {
    return channels?.map((channel) => {
      return messageChannelComponents?.find((item) => item.name === channel)?.element
    })
  }

  const validateForm = useCallback(() => {
    if (configType === 'normal') {
      return config?.desc?.length && config?.channels?.length && messageChannels?.length
    } else {
      return config?.desc?.length && config?.desc?.length > 0
    }
  }, [configType, config?.desc?.length, config?.channels?.length, messageChannels?.length])

  const onConfirm = useCallback(() => {
    setValidate(true)
    if (validateForm()) {
      if (configType === 'normal') {
        const finnalChannels = [...config?.channels]
        for (const channel of finnalChannels) {
          channel.addresses = channel?.addresses?.map((address) => ({
            address: address?.address,
            sign: address?.sign ?? '',
          }))
        }

        const finalOrgs = config?.org_ids?.filter((org_id) =>
          orgs?.data?.map((data) => data.id)?.includes(org_id as number),
        )

        const currentConfig = {
          ...config,
          org_ids: finalOrgs as string[] | number[],
          channels: finnalChannels,
        }
        if (!!formId) {
          updateMutate({ id: formId, ...currentConfig })
        } else {
          createMutate(currentConfig)
        }
      } else {
        const finalOrgs = config?.org_ids?.filter((org_id) =>
          orgs?.data?.map((data) => data.id)?.includes(org_id as number),
        )

        const currentConfig = {
          ...config,
          org_ids: finalOrgs as string[] | number[],
        }
        if (!!formId) {
          updateMutate({ id: formId, ...currentConfig })
        } else {
          createMutate(config)
        }
      }
    }
  }, [config, configType, createMutate, formId, orgs?.data, updateMutate, validateForm])

  const handleUpdateDesc = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event?.target?.value
    setConfig((config) => ({ ...config, desc: value }))
  }, [])

  const handleUpdateOrgs = useCallback((value: string[]) => {
    setConfig((config) => ({ ...config, org_ids: value }))
  }, [])

  const handleUpdateApps = useCallback((value: string[]) => {
    setConfig((config) => ({ ...config, app_ids: value }))
  }, [])

  const getOrgOptions = useMemo(() => {
    const defaultOrgs =
      orgs?.data?.map((item) => ({
        label: item.name,
        value: item.id,
      })) ?? []
    const configOrgs = configRes?.orgs?.map((org) => ({
      label: org.name,
      value: org.id,
    }))

    const disabledOrg = configOrgs?.find(
      (org) => !defaultOrgs?.map((defaultOrg) => defaultOrg?.value)?.includes(org?.value),
    )
    if (disabledOrg) {
      //@ts-ignore
      disabledOrg.disabled = true
      defaultOrgs.push(disabledOrg)
    }

    return defaultOrgs
  }, [configRes, orgs])

  useLayoutEffect(() => {
    if (messageChannels?.length > 0) {
      const target = document.getElementById(messageChannels[messageChannels?.length - 1])
      target?.scrollIntoView({ behavior: 'smooth', inline: 'end' })
    }
  }, [messageChannels])

  return (
    <PlainCard sx={{ gap: '8px' }}>
      <Text weight='bold'>{pathObject?.path === 'createConfig' ? '创建消息通知策略' : '修改消息通知策略'}</Text>
      <ScrollY>
        <Stack sx={{ gap: '8px' }}>
          <FormControl key='config-desc' margin='dense'>
            <Row sx={{ gap: '16px', alignItems: 'center' }}>
              <TextField
                disabled={configType === 'station' || configType === 'wechat'}
                placeholder='简单描述此次推送的目的，如：推送网页检测的告警信息'
                sx={{ flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <FormLabel sx={{ marginRight: '12px', width: '88px' }} required={true}>
                      规则描述
                    </FormLabel>
                  ),
                }}
                value={config?.desc}
                onChange={handleUpdateDesc}
              />
            </Row>
            {validate && config?.desc?.length === 0 && (
              <FormHelperText sx={{ marginLeft: 0 }} error={true}>
                请填写规则描述
              </FormHelperText>
            )}
          </FormControl>
          <FormControl key='config-org' margin='dense'>
            <TagInput
              select={true}
              label={'空间'}
              required={false}
              tagVariant={'text'}
              // size={max}
              value={config?.org_ids as string[]}
              onChange={handleUpdateOrgs}
              placeholder={'仅对这些空间进行消息通知，未选择则默认通知所有空间'}
              options={getOrgOptions}
            />
          </FormControl>
          <FormControl key='config-app' margin='dense'>
            <TagInput
              select={true}
              label={'产品应用'}
              required={false}
              tagVariant={'text'}
              // size={max}
              value={config?.app_ids as string[]}
              onChange={handleUpdateApps}
              placeholder={'仅对这些产品应用进行消息通知，未选择则默认通知所有产品应用'}
              options={
                market?.data?.map((item) => ({
                  label: item.name,
                  value: item.id,
                })) ?? []
              }
            />
          </FormControl>
          {!(configType === 'station' || configType === 'wechat') && (
            <FormControl key='config-channel-types' margin='dense'>
              <Row sx={{ gap: '16px', alignItems: 'center' }}>
                <FormLabel required={true}>接收渠道</FormLabel>
                <FormGroup
                  sx={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '16px',
                  }}
                >
                  <LabeledCheckbox
                    key='config-channel-email'
                    label='邮箱'
                    control={
                      <Checkbox
                        icon={<CheckIcon />}
                        sx={{ padding: '0px 8px' }}
                        checked={config?.channels?.findIndex((channel) => channel.channel === 'email') > -1}
                        //eslint-disable-next-line react/jsx-no-bind
                        onChange={(event, checked) => handleChangeMessageChannels('email', checked)}
                      />
                    }
                  />
                  <LabeledCheckbox
                    key='config-channel-dingtalk'
                    label='钉钉'
                    control={
                      <Checkbox
                        icon={<CheckIcon />}
                        sx={{ padding: '0px 8px' }}
                        checked={config?.channels?.findIndex((channel) => channel.channel === 'dingtalk') > -1}
                        //eslint-disable-next-line react/jsx-no-bind
                        onChange={(event, checked) => handleChangeMessageChannels('dingtalk', checked)}
                      />
                    }
                  />
                  <LabeledCheckbox
                    key='config-channel-wecom'
                    sx={{ width: '110px' }}
                    label='企业微信'
                    control={
                      <Checkbox
                        icon={<CheckIcon />}
                        sx={{ padding: '0px 8px' }}
                        checked={config?.channels?.findIndex((channel) => channel.channel === 'wecom') > -1}
                        //eslint-disable-next-line react/jsx-no-bind
                        onChange={(event, checked) => handleChangeMessageChannels('wecom', checked)}
                      />
                    }
                  />
                  <LabeledCheckbox
                    key='config-channel-lark'
                    label='飞书'
                    control={
                      <Checkbox
                        icon={<CheckIcon />}
                        sx={{ padding: '0px 8px' }}
                        checked={config?.channels?.findIndex((channel) => channel.channel === 'lark') > -1}
                        //eslint-disable-next-line react/jsx-no-bind
                        onChange={(event, checked) => handleChangeMessageChannels('lark', checked)}
                      />
                    }
                  />
                  <LabeledCheckbox
                    key='config-channel-syslog'
                    label='syslog'
                    control={
                      <Checkbox
                        icon={<CheckIcon />}
                        sx={{ padding: '0px 8px' }}
                        checked={config?.channels?.findIndex((channel) => channel.channel === 'syslog') > -1}
                        //eslint-disable-next-line react/jsx-no-bind
                        onChange={(event, checked) => handleChangeMessageChannels('syslog', checked)}
                      />
                    }
                  />
                </FormGroup>
              </Row>
              {validate && messageChannels?.length === 0 && (
                <FormHelperText sx={{ marginLeft: 0 }} error={true}>
                  请选择接收通道
                </FormHelperText>
              )}
            </FormControl>
          )}
          {!(configType === 'station' || configType === 'wechat') && renderMessageChannelForm(messageChannels)}
        </Stack>
      </ScrollY>
      <Row sx={{ justifyContent: 'flex-end', gap: '8px' }}>
        <Button type='secondary' onClick={onCancel}>
          取消
        </Button>
        <Button
          // disabled={!validateForm()}
          onClick={onConfirm}
          type='primary'
        >
          确定
        </Button>
      </Row>
    </PlainCard>
  )
}

export default ConfigForm
